import dayjs from "dayjs";
import type { BaseField, Config } from "../types";

const DYNAMIC_DATE_TYPES = new Set([
  "date",
  "datetime",
  "date_range",
  "datetime_range",
]);

function resolveRelativeOffset(
  offset: {
    value: number;
    unit: "days" | "months" | "years";
    direction: "before" | "after";
  },
  withTime: boolean,
): string {
  const base =
    offset.direction === "before"
      ? dayjs().subtract(offset.value, offset.unit)
      : dayjs().add(offset.value, offset.unit);
  return withTime ? base.format("DD/MM/YYYY HH:mm") : base.format("DD/MM/YYYY");
}

function applyDynamicDateConfig(
  config: Config,
  today: string,
  todayWithTime: string,
): void {
  if (config.min_date === "now") config.min_date = today;
  if (config.max_date === "now") config.max_date = today;
  if (config.min_datetime === "now") config.min_datetime = todayWithTime;
  if (config.max_datetime === "now") config.max_datetime = todayWithTime;

  const includeTime = config.includeTime;

  if (config.min_relative_offset) {
    const resolved = resolveRelativeOffset(
      config.min_relative_offset,
      !!includeTime,
    );
    if (includeTime) {
      config.min_datetime = resolved;
    } else {
      config.min_date = resolved;
    }
    delete config.min_relative_offset;
  }

  if (config.max_relative_offset) {
    const resolved = resolveRelativeOffset(
      config.max_relative_offset,
      !!includeTime,
    );
    if (includeTime) {
      config.max_datetime = resolved;
    } else {
      config.max_date = resolved;
    }
    delete config.max_relative_offset;
  }
}

function applyDynamicYearConfig(config: Config, currentYear: number): void {
  if (config.min_year === "current") config.min_year = currentYear;
  if (config.max_year === "current") config.max_year = currentYear;
  if (config.year_range_type === "relative") {
    config.min_year = currentYear + (config.year_offset_min ?? 0);
    config.max_year = currentYear + (config.year_offset_max ?? 0);
  }
}

export function applyDynamicFieldConfig(
  field: BaseField,
  today: string,
  todayWithTime: string,
  currentYear: number,
) {
  const config = field.config;
  if (!config) return field;

  const isDateType = DYNAMIC_DATE_TYPES.has(field.type ?? "");
  const isYearType = field.type === "year";

  if (!isDateType && !isYearType) return field;

  const newConfig = { ...config };
  if (isDateType) applyDynamicDateConfig(newConfig, today, todayWithTime);
  if (isYearType) applyDynamicYearConfig(newConfig, currentYear);

  return { ...field, config: newConfig };
}
